import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook that manages portal-based dropdown positioning.
 * Escapes CSS transform context by calculating viewport coordinates
 * from the toggle element's bounding rect.
 *
 * @param {boolean} open - Whether the dropdown is open
 * @param {React.RefObject} toggleRef - Ref to the toggle button element
 * @param {function} onClose - Callback to close the dropdown
 * @returns {{ menuRef: React.RefObject, setMenuRef: function, menuStyle: object|null }}
 */
export function usePortalDropdown(open, toggleRef, onClose) {
  const menuRef = useRef(null);
  const [position, setPosition] = useState(null);
  const onCloseRef = useRef(onClose);
  const rafId = useRef(null);

  onCloseRef.current = onClose;

  const setMenuRef = useCallback((node) => {
    menuRef.current = node;
  }, []);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        if (!toggleRef.current) return;
        const rect = toggleRef.current.getBoundingClientRect();
        setPosition({ top: rect.bottom, left: rect.left, minWidth: rect.width });
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [open, toggleRef]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target)
        && toggleRef.current && !toggleRef.current.contains(e.target)
      ) {
        onCloseRef.current();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, toggleRef]);

  const menuStyle = open && position ? {
    position: 'fixed',
    top: position.top,
    left: position.left,
    minWidth: position.minWidth,
    zIndex: 1,
  } : null;

  return { menuRef, setMenuRef, menuStyle };
}
